import React from 'react';
import Icon from '../AppIcon';

const BookingProgress = ({ 
  currentStep = 1, 
  totalSteps = 4, 
  steps = [],
  onStepClick = null 
}) => {
  const defaultSteps = [
    { id: 1, title: 'Service Selection', description: 'Choose your photography service' },
    { id: 2, title: 'Date & Time', description: 'Select preferred date and time' },
    { id: 3, title: 'Details', description: 'Provide session details' },
    { id: 4, title: 'Confirmation', description: 'Review and confirm booking' }
  ];

  const progressSteps = steps?.length > 0 ? steps : defaultSteps;

  const getStepStatus = (stepNumber) => {
    if (stepNumber < currentStep) return 'completed';
    if (stepNumber === currentStep) return 'current';
    return 'upcoming';
  };

  const getStepIcon = (stepNumber, status) => {
    if (status === 'completed') return 'Check';
    if (status === 'current') return 'Circle';
    return 'Circle';
  };

  const handleStepClick = (stepNumber) => {
    if (onStepClick && stepNumber <= currentStep) {
      onStepClick(stepNumber);
    }
  };

  return (
    <div className="w-full">
      {/* Desktop Progress Bar */}
      <div className="hidden md:block">
        <div className="flex items-center justify-between mb-8">
          {progressSteps?.map((step, index) => {
            const stepNumber = index + 1;
            const status = getStepStatus(stepNumber);
            const isClickable = onStepClick && stepNumber <= currentStep;

            return (
              <div key={step?.id} className="flex items-center flex-1">
                {/* Step Circle */}
                <div 
                  className={`
                    flex items-center justify-center w-10 h-10 rounded-full border-2 transition-smooth
                    ${status === 'completed' 
                      ? 'bg-success border-success text-success-foreground' 
                      : status === 'current' ?'bg-accent border-accent text-accent-foreground' :'bg-background border-border text-muted-foreground'
                    }
                    ${isClickable ? 'cursor-pointer hover:scale-105' : ''}
                  `}
                  onClick={() => handleStepClick(stepNumber)}
                >
                  <Icon 
                    name={getStepIcon(stepNumber, status)} 
                    size={status === 'completed' ? 16 : 20}
                    strokeWidth={status === 'current' ? 3 : 2}
                  />
                </div>
                {/* Step Content */}
                <div className="ml-4 flex-1">
                  <h3 
                    className={`
                      text-sm font-body font-medium
                      ${status === 'current' ? 'text-accent' : 'text-foreground'}
                      ${isClickable ? 'cursor-pointer hover:text-accent' : ''}
                    `}
                    onClick={() => handleStepClick(stepNumber)}
                  >
                    {step?.title}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {step?.description}
                  </p>
                </div>
                {/* Connector Line */}
                {index < progressSteps?.length - 1 && (
                  <div className="flex-1 mx-4">
                    <div 
                      className={`
                        h-0.5 transition-smooth
                        ${stepNumber < currentStep ? 'bg-success' : 'bg-border'}
                      `}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {/* Mobile Progress Bar */}
      <div className="md:hidden mb-6">
        {/* Progress Bar */}
        <div className="flex items-center mb-4">
          <div className="flex-1 bg-border rounded-full h-2">
            <div 
              className="bg-accent h-2 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
          <span className="ml-3 text-sm font-body font-medium text-muted-foreground">
            {currentStep} of {totalSteps}
          </span>
        </div>

        {/* Current Step Info */}
        <div className="text-center">
          <h3 className="text-lg font-heading font-medium text-accent mb-1">
            {progressSteps?.[currentStep - 1]?.title}
          </h3>
          <p className="text-sm text-muted-foreground">
            {progressSteps?.[currentStep - 1]?.description}
          </p>
        </div>

        {/* Step Indicators */}
        <div className="flex justify-center space-x-2 mt-4">
          {progressSteps?.map((_, index) => {
            const stepNumber = index + 1;
            const status = getStepStatus(stepNumber);
            
            return (
              <div
                key={index}
                className={`
                  w-2 h-2 rounded-full transition-smooth
                  ${status === 'completed' || status === 'current' 
                    ? 'bg-accent' :'bg-border'
                  }
                `}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BookingProgress;